// ── SECURE CONFIG ─────────────────────────────────────────────────────────────
// Zero hardcoded credentials. All sensitive values live in localStorage only.

export const STORAGE_KEYS = {
  APPS_SCRIPT_URL: 'et_apps_script_url',
  SECRET_TOKEN:    'et_secret_token',
  SALARY:          'et_salary',
  CASH_FAYSAL:     'et_cash_faysal',
  CASH_INVEST:     'et_cash_invest',
  BUDGET_PLAN:     'et_budget_plan',
};

export const getConfig = () => ({
  appsScriptUrl: localStorage.getItem(STORAGE_KEYS.APPS_SCRIPT_URL) || '',
  secretToken:   localStorage.getItem(STORAGE_KEYS.SECRET_TOKEN) || '',
});

export const saveConfig = ({ appsScriptUrl, secretToken }) => {
  localStorage.setItem(STORAGE_KEYS.APPS_SCRIPT_URL, appsScriptUrl);
  localStorage.setItem(STORAGE_KEYS.SECRET_TOKEN, secretToken);
};

export const clearConfig = () => Object.values(STORAGE_KEYS).forEach(k => localStorage.removeItem(k));
export const isConfigured = () => { const c = getConfig(); return !!(c.appsScriptUrl && c.secretToken); };

// ── BANK / ACCOUNT DEFINITIONS ────────────────────────────────────────────────
// Maps all possible Shortcuts bank name variations to display info.
// Keys match exactly what your iPhone Shortcuts send in the "bank" field.
export const ACCOUNTS = {
  // HSBC Account (030-545)
  'HSBC-Account':         { label: 'HSBC Account',            color: '#c41e3a', type: 'bank', balanceFromSMS: true  },

  // HSBC Card - Taimoor (9397) — various Shortcuts name formats
  'HSBC-Card-9397':       { label: 'HSBC Card ••9397',        color: '#e8253a', type: 'card', balanceFromSMS: true  },
  'HSBC-Card-TALAM':      { label: 'HSBC Card ••9397',        color: '#e8253a', type: 'card', balanceFromSMS: true  },
  'HSBC-CARD-TALAM':      { label: 'HSBC Card ••9397',        color: '#e8253a', type: 'card', balanceFromSMS: true  },
  'HSBC-Credit-Card-9397':{ label: 'HSBC Card ••9397',        color: '#e8253a', type: 'card', balanceFromSMS: true  },

  // HSBC Card - Zayd (4317)
  'HSBC-Card-4317':       { label: 'HSBC Card ••4317 (Zayd)', color: '#ff6b6b', type: 'card', balanceFromSMS: false, owner: 'Zayd' },
  'HSBC-Card-ZAYD':       { label: 'HSBC Card ••4317 (Zayd)', color: '#ff6b6b', type: 'card', balanceFromSMS: false, owner: 'Zayd' },
  'HSBC-CARD-ZAYD':       { label: 'HSBC Card ••4317 (Zayd)', color: '#ff6b6b', type: 'card', balanceFromSMS: false, owner: 'Zayd' },
  'HSBC-Credit-Card-4317':{ label: 'HSBC Card ••4317 (Zayd)', color: '#ff6b6b', type: 'card', balanceFromSMS: false, owner: 'Zayd' },

  // Standard Chartered - Taimoor (6100)
  'StanChart-Card-6100':    { label: 'StanChart ••6100',      color: '#0a5c36', type: 'card', balanceFromSMS: false },
  'STANCHART-CARD-TALAM':   { label: 'StanChart ••6100',      color: '#0a5c36', type: 'card', balanceFromSMS: false },
  'StanChart-Card-TALAM':   { label: 'StanChart ••6100',      color: '#0a5c36', type: 'card', balanceFromSMS: false },
  'StanChart-CARD-6100':    { label: 'StanChart ••6100',      color: '#0a5c36', type: 'card', balanceFromSMS: false },

  // Standard Chartered - Wife (2390)
  'StanChart-Card-2390':    { label: 'StanChart ••2390 (Wife)', color: '#1a8a52', type: 'card', balanceFromSMS: false, owner: 'Wife' },
  'STANCHART-CARD-WIFE':    { label: 'StanChart ••2390 (Wife)', color: '#1a8a52', type: 'card', balanceFromSMS: false, owner: 'Wife' },
  'StanChart-Card-WIFE':    { label: 'StanChart ••2390 (Wife)', color: '#1a8a52', type: 'card', balanceFromSMS: false, owner: 'Wife' },

  // ADCB Account (160001)
  'ADCB-Account':           { label: 'ADCB Account', color: '#f47920', type: 'bank', balanceFromSMS: true },
  'ADCB-ACCOUNT':           { label: 'ADCB Account', color: '#f47920', type: 'bank', balanceFromSMS: true },

  // Emirates Islamic (2793)
  'EIB-Card-2793':          { label: 'Emirates Islamic ••2793', color: '#006633', type: 'card', balanceFromSMS: false },
  'EIB-CARD-2793':          { label: 'Emirates Islamic ••2793', color: '#006633', type: 'card', balanceFromSMS: false },
  'EIL-CARD':               { label: 'Emirates Islamic ••2793', color: '#006633', type: 'card', balanceFromSMS: false },
};

// Manual cash accounts
export const CASH_ACCOUNTS = [
  { key: 'HSBC-Account',  label: 'HSBC 030-545',  auto: true  },
  { key: 'ADCB-Account',  label: 'ADCB 160001',   auto: true  },
  { key: 'faysal',        label: 'Faysal Bank',   auto: false },
  { key: 'invest_nation', label: 'Invest Nation',  auto: false },
];

// ── CATEGORIES ────────────────────────────────────────────────────────────────
export const CATEGORIES = [
  { category: 'Food',           subcategory: 'Groceries'         },
  { category: 'Food',           subcategory: 'Dining Out'        },
  { category: 'Food',           subcategory: 'Cafes'             },
  { category: 'Food',           subcategory: 'Delivery'          },
  { category: 'Transport',      subcategory: 'Fuel'              },
  { category: 'Transport',      subcategory: 'Taxi/Ride'         },
  { category: 'Transport',      subcategory: 'Parking'           },
  { category: 'Shopping',       subcategory: 'Retail'            },
  { category: 'Shopping',       subcategory: 'Online'            },
  { category: 'Utilities',      subcategory: 'Electricity'       },
  { category: 'Utilities',      subcategory: 'Internet/Phone'    },
  { category: 'Housing',        subcategory: 'Rent'              },
  { category: 'Housing',        subcategory: 'Maintenance'       },
  { category: 'Health',         subcategory: 'Pharmacy'          },
  { category: 'Health',         subcategory: 'Doctor'            },
  { category: 'Health',         subcategory: 'Gym'               },
  { category: 'Education',      subcategory: 'School Fees'       },
  { category: 'Education',      subcategory: 'University'        },
  { category: 'Entertainment',  subcategory: 'Subscriptions'     },
  { category: 'Entertainment',  subcategory: 'Cinema/Events'     },
  { category: 'Travel',         subcategory: 'Hotels'            },
  { category: 'Travel',         subcategory: 'Flights'           },
  { category: 'Transfers',      subcategory: 'Internal Transfer' },
  { category: 'Transfers',      subcategory: 'External Transfer' },
  { category: 'Committed',      subcategory: 'University Fees'   },
  { category: 'Committed',      subcategory: 'Rent'              },
  { category: 'Committed',      subcategory: 'Other Committed'   },
  { category: 'Income',         subcategory: 'Salary'            },
  { category: 'Other',          subcategory: 'Miscellaneous'     },
];

// Main page / trends roll every category up into these six. Anything else → Other.
export const MAIN_CATEGORIES = ['Food', 'Shopping', 'Transport', 'Entertainment', 'Utilities', 'Other'];
export const mainCategory = cat => MAIN_CATEGORIES.includes(cat) ? cat : 'Other';

// Fixed order, validated for colour-blind separation on the dark surface
export const CATEGORY_COLORS = {
  Food:          '#3987e5',
  Shopping:      '#d95926',
  Transport:     '#199e70',
  Entertainment: '#c98500',
  Utilities:     '#d55181',
  Other:         '#9085e9',
};

// ── MONTHLY BUDGET ────────────────────────────────────────────────────────────
// Defaults only — user edits are saved to localStorage (STORAGE_KEYS.BUDGET_PLAN).
// `category` links a line to card spend so the Budget page can show actuals.
export const DEFAULT_BUDGET = {
  total: 85000,
  items: [
    { key: 'maid',      label: 'Maid expense',   amount: 3100  },
    { key: 'car',       label: 'Car payment',    amount: 3400  },
    { key: 'transport', label: 'Transport/Fuel', amount: 1500,  category: 'Transport' },
    { key: 'utilities', label: 'Utilities',      amount: 3000,  category: 'Utilities' },
    { key: 'rent',      label: 'House rent',     amount: 13500 },
    { key: 'food',      label: 'Food',           amount: 8000,  category: 'Food'      },
    { key: 'other',     label: 'Other',          amount: 3000,  category: 'Other'     },
  ],
};

export const getBudgetPlan = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEYS.BUDGET_PLAN));
    if (!saved) return DEFAULT_BUDGET;
    return {
      total: Number(saved.total) || DEFAULT_BUDGET.total,
      items: DEFAULT_BUDGET.items.map(i => ({ ...i, amount: saved.items?.[i.key] ?? i.amount })),
    };
  } catch { return DEFAULT_BUDGET; }
};

export const saveBudgetPlan = plan => localStorage.setItem(STORAGE_KEYS.BUDGET_PLAN, JSON.stringify({
  total: plan.total,
  items: Object.fromEntries(plan.items.map(i => [i.key, i.amount])),
}));

// ── DATE HELPERS ──────────────────────────────────────────────────────────────
// Expense month runs 19th -> 18th
export function getExpenseMonth(date = new Date()) {
  const d = new Date(date);
  // If day >= 19, month period started on 19th of current month
  // If day <= 18, month period started on 19th of previous month
  if (d.getDate() >= 19) {
    return { year: d.getFullYear(), month: d.getMonth(), label: formatMonthLabel(d.getFullYear(), d.getMonth()) };
  } else {
    const prev = new Date(d.getFullYear(), d.getMonth() - 1, 1);
    return { year: prev.getFullYear(), month: prev.getMonth(), label: formatMonthLabel(prev.getFullYear(), prev.getMonth()) };
  }
}

export function formatMonthLabel(year, month) {
  return new Date(year, month, 1).toLocaleString('en-AE', { month: 'long', year: 'numeric' });
}
export function parseTxDate(v) {
  if (!v) return null;
  const s = String(v);
  if (s.includes('T') || (s.includes('-') && !s.includes('/'))) {
    const d = new Date(s);
    return isNaN(d) ? null : d;
  }
  const parts = s.split('/');
  if (parts.length === 3) {
    return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
  }
  return null;
}

export function isInExpensePeriod(txDateStr, periodYear, periodMonth) {
  const txDate = parseTxDate(txDateStr);
  if (!txDate) return false;
  const start = new Date(periodYear, periodMonth, 19);
  const end   = new Date(periodYear, periodMonth + 1, 18, 23, 59, 59);
  return txDate >= start && txDate <= end;
}

export function isToday(txDateStr) {
  const d = parseTxDate(txDateStr);
  if (!d) return false;
  const t = new Date();
  return d.getDate() === t.getDate() && d.getMonth() === t.getMonth() && d.getFullYear() === t.getFullYear();
}

export function isThisCalendarWeek(txDateStr) {
  const txDate = parseTxDate(txDateStr);
  if (!txDate) return false;
  const now    = new Date();
  const monday = new Date(now);
  monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
  monday.setHours(0, 0, 0, 0);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);
  return txDate >= monday && txDate <= sunday;
}

export function getWeekLabel() {
  const now    = new Date();
  const monday = new Date(now);
  monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const fmt = d => d.toLocaleDateString('en-AE', { day: 'numeric', month: 'short' });
  return `${fmt(monday)} – ${fmt(sunday)}`;
}